import { Patterns } from "@omni/schemas/input/patterns";
import { ChangeEvent } from "react";
import {
    ControllerRenderProps,
    FieldValues,
    Path,
    PathValue,
    useFormContext
} from "react-hook-form";

import { Input } from "@/components/Form/Base/input.component";
import { Config } from "@/utils/config.utils";

import { useAutofill } from "../../Utils/useAutofill";
import { BaseField } from "../BaseField/BaseField.component";
import { FormValuesExploded } from "../formField.types";

type GiftCardFieldProps<T extends FieldValues> = FormValuesExploded<T> & {
    disabled?: boolean;
};

const GiftCardField = <T extends FieldValues>({
    name,
    label,
    microCopy,
    required,
    hideMicroCopy,
    hideLabel,
    disabled,
    test
}: GiftCardFieldProps<T>) => {
    const { setValue } = useFormContext<T>();
    const { handleOnAutofill } = useAutofill<T>(name);

    const pattern = Patterns[Config.general.basestore].giftCard;

    const handleChange = (
        oldValue: PathValue<T, Path<T>>,
        e: ChangeEvent<HTMLInputElement>,
        onChangeCB: ControllerRenderProps<T, Path<T>>["onChange"]
    ) => {
        const { value = "", validity } = e.currentTarget;

        if (!value && !validity.valid) {
            e.preventDefault();
            setValue(name, oldValue);
            return;
        }

        onChangeCB?.(e);
    };

    const onPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
        e.preventDefault();
        if (e.clipboardData?.getData) {
            setValue(
                name,
                e.clipboardData.getData("text/plain").trim() as PathValue<
                    T,
                    Path<T>
                >
            );
        }
    };

    return (
        <BaseField<T, Path<T>>
            name={name}
            label={label}
            microCopy={microCopy}
            required={required}
            hideLabel={hideLabel}
            hideMicroCopy={hideMicroCopy}
        >
            {({ field, fieldState }) => {
                return (
                    <Input
                        {...field}
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                            handleChange(field.value, e, field.onChange)
                        }
                        type="text"
                        pattern={pattern.source}
                        id={name}
                        valid={fieldState.isValid}
                        invalid={fieldState.isInvalid}
                        disabled={disabled}
                        data-test={test}
                        inputMode="text"
                        onAutofill={handleOnAutofill}
                        onPaste={onPaste}
                    />
                );
            }}
        </BaseField>
    );
};

export { GiftCardField };
